import { forwardRef, useImperativeHandle, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileToDataUrl } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X, MapPin, ImagePlus, Sparkles, AlertTriangle } from "lucide-react";
import MultipackPreview from "@/components/MultipackPreview";
import CoopItemsEditor from "@/components/CoopItemsEditor";
import { isCoopType, coopTotals } from "@/lib/coop";

const CATEGORIES = ["General", "Groceries", "Food", "Health", "Beauty", "Footwear", "Clothing",
  "Electronics", "Home", "Toys", "Sports", "Automotive", "Entertainment", "Other"];
const PROVINCES = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
  "Northwest Territories", "Nunavut", "Yukon"];
const BOTH_PRESENT_TERM = "Both the buyer and the deal partner must be present together in-store when the item(s) are purchased. The deal partner may waive this in-person requirement.";

const EMPTY = {
  title: "", description: "", retailer: "", product_name: "", category: "General",
  image_url: "", deal_type: "Buy One Get One Free", price_type: "fixed",
  original_price: "", deal_price: "", terms: "", source: "",
  location_scope: "canada_wide", province: "", city: "", address: "",
  expires_at: "", both_present_required: true, line_items: [],
};

// Self-contained "publish / edit a curated deal" dialog.
// Parent opens it imperatively via ref.open(deal|null) and passes onSaved to refresh.
const DealFormDialog = forwardRef(({ onSaved }, ref) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flyerWarnings, setFlyerWarnings] = useState([]);
  const [extractingFlyer, setExtractingFlyer] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useImperativeHandle(ref, () => ({
    open: (deal) => {
      if (deal) {
        setForm({
          title: deal.title || "", description: deal.description || "", retailer: deal.retailer || "",
          product_name: deal.product_name || "", category: deal.category || "General",
          image_url: deal.image_url || "", deal_type: deal.deal_type || "Buy One Get One Free",
          price_type: deal.price_type || "fixed",
          original_price: deal.original_price ?? "", deal_price: deal.deal_price ?? "",
          terms: deal.terms || "", source: deal.source || "",
          location_scope: deal.location_scope || "canada_wide", province: deal.province || "",
          city: deal.city || "", address: deal.address || "", expires_at: deal.expires_at || "",
          both_present_required: deal.both_present_required !== false,
          line_items: Array.isArray(deal.line_items) ? deal.line_items : [],
        });
        setEditingId(deal.deal_id);
      } else {
        setForm(EMPTY);
        setEditingId(null);
      }
      setFlyerWarnings([]);
      setOpen(true);
    },
  }));

  const saveDeal = async () => {
    setBusy(true);
    try {
      const coop = isCoopType(form.deal_type);
      const body = {
        ...form,
        category: form.category?.trim() || "General",
        price_type: coop ? "fixed" : form.price_type,
        original_price: !coop && form.price_type === "tbc" ? 0 : parseFloat(form.original_price || 0),
        deal_price: !coop && form.price_type === "tbc" ? 0 : parseFloat(form.deal_price || 0),
        line_items: coop ? (form.line_items || []).filter((it) => String(it.name || "").trim()) : null,
      };
      if (coop && !body.line_items.length) {
        toast.error("Add at least one product to the co-op basket");
        setBusy(false);
        return;
      }
      if (editingId) {
        await api.put(`/deals/${editingId}`, body);
        toast.success("Deal updated");
      } else {
        await api.post("/admin/deals", body);
        toast.success("Deal published");
      }
      setForm(EMPTY);
      setEditingId(null);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const onImageFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, image_url: dataUrl }));
      toast.success("Image attached");
    } catch {
      toast.error("Could not read image");
    }
  };

  const onImagePaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    if (item) { e.preventDefault(); onImageFile(item.getAsFile()); }
  };

  const uploadFlyer = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setExtractingFlyer(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/deals/extract-flyer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      setForm((prev) => ({
        ...prev,
        title: data.title || prev.title,
        retailer: data.retailer || prev.retailer,
        product_name: data.product_name || prev.product_name,
        category: data.category || prev.category,
        deal_type: data.deal_type || prev.deal_type,
        original_price: data.original_price || prev.original_price,
        deal_price: data.deal_price || prev.deal_price,
        description: data.description || prev.description,
        expires_at: data.expiry_date || prev.expires_at,
        image_url: data.image_path ? BACKEND + data.image_path : prev.image_url,
      }));
      toast.success("Flyer read — fields pre-filled. Review & edit before saving.");
      setFlyerWarnings(data.warnings || []);
      if ((data.warnings || []).length) {
        toast.warning(`Double-check: ${data.warnings.map((w) => w.label).join(", ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read the flyer. Fill the fields manually.");
    } finally {
      setExtractingFlyer(false);
      e.target.value = "";
    }
  };

  const isMulti = isCoopType(form.deal_type);
  const coopComputed = coopTotals(form.line_items || []);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY); setFlyerWarnings([]); } }}>
      <DialogTrigger asChild>
        <Button data-testid="new-deal-btn" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200">
          <Plus className="mr-1 h-4 w-4" /> New deal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit deal" : "Publish a curated deal"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Fill in the deal details, or upload a flyer to auto-fill them.</DialogDescription>
        <div className="space-y-3">
          <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
            <input type="file" accept="image/jpeg,image/png,image/webp" id="admin-flyer-input"
              data-testid="admin-flyer-input" onChange={uploadFlyer} className="hidden" />
            <Button type="button" variant="outline" size="sm" disabled={extractingFlyer}
              data-testid="admin-flyer-btn"
              onClick={() => document.getElementById("admin-flyer-input").click()}
              className="w-full rounded-full border-primary/30">
              <Sparkles className="mr-1.5 h-4 w-4" />
              {extractingFlyer ? "Reading flyer…" : "Upload a flyer photo — AI auto-fills the fields"}
            </Button>
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              Upload the deal flyer and we'll pre-fill prices, product & type (all editable). The flyer becomes the deal image.
            </p>
          </div>
          {flyerWarnings.length > 0 && (
            <div data-testid="flyer-warnings" className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800">
              <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Please double-check these fields</div>
              <p className="mt-0.5">The AI wasn't sure about: <b>{flyerWarnings.map((w) => w.label).join(", ")}</b>.</p>
            </div>
          )}
          <div><Label>Title *</Label><Input data-testid="deal-title-input" value={form.title} onChange={set("title")} placeholder="BOGO Free — Coffee Beans" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Retailer *</Label><Input data-testid="deal-retailer-input" value={form.retailer} onChange={set("retailer")} /></div>
            <div>
              <Label>Category</Label>
              <Select value={CATEGORIES.includes(form.category) ? form.category : "Other"}
                onValueChange={(v) => setVal("category", v === "Other" ? "" : v)}>
                <SelectTrigger data-testid="deal-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!CATEGORIES.includes(form.category) && (
            <div><Label>Custom category</Label><Input data-testid="deal-category-other-input" value={form.category} onChange={set("category")} placeholder="Enter category" /></div>
          )}
          <div><Label>Product name</Label><Input value={form.product_name} onChange={set("product_name")} /></div>
          <div><Label>Deal type</Label>
            <select data-testid="deal-type-select" value={form.deal_type} onChange={set("deal_type")}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option>Buy One Get One Free</option>
              <option>Buy One, Get One at a Discount</option>
              <option>Bulk Buying Co-op</option>
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {isMulti
                ? "Bulk Buying Co-op: add each product in the wholesale pack below. Set each item's unit price and how much of it the partner takes (by quantity, %, or a fixed $). A flat fee applies to BOTH users; the owner's is deducted from their payout."
                : form.deal_type?.toLowerCase().includes("discount")
                ? "Discount: set Full price (original) + 2nd item's discounted price. Partner pays amount paid + ½ savings + tax + fees."
                : "Free: set the cost of goods in the deal price. Partner pays ½ of it + tax + fees."}
            </p>
          </div>

          <div>
            <Label>Pricing</Label>
            <div className="mt-1 inline-flex rounded-md border border-black/10 p-0.5">
              <button type="button" data-testid="price-mode-fixed"
                onClick={() => setVal("price_type", "fixed")}
                disabled={isMulti}
                className={`px-3 py-1 text-sm rounded ${form.price_type === "fixed" ? "bg-foreground text-background" : "text-muted-foreground"} ${isMulti ? "opacity-40 cursor-not-allowed" : ""}`}>Fixed</button>
              <button type="button" data-testid="price-mode-tbc"
                onClick={() => setVal("price_type", "tbc")}
                disabled={isMulti}
                className={`px-3 py-1 text-sm rounded ${form.price_type === "tbc" ? "bg-foreground text-background" : "text-muted-foreground"} ${isMulti ? "opacity-40 cursor-not-allowed" : ""}`}>To be calculated</button>
            </div>
          </div>
          {isMulti ? (
            <>
              <CoopItemsEditor
                items={form.line_items || []}
                onChange={(items) => setVal("line_items", items)}
                idPrefix="admin-coop"
              />
              {coopComputed.priceTotal > 0 && (
                <MultipackPreview dealType={form.deal_type} dealPrice={coopComputed.shareTotal}
                  originalPrice={coopComputed.priceTotal} province={form.province || undefined} />
              )}
            </>
          ) : form.price_type === "fixed" ? (
            <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full price ($)</Label><Input data-testid="deal-price-input" type="number" value={form.original_price} onChange={set("original_price")} /></div>
              <div><Label>2nd item price ($)</Label><Input type="number" value={form.deal_price} onChange={set("deal_price")} placeholder="0 for free" /></div>
            </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground rounded-md bg-secondary p-2">Prices shown as "To be calculated" — the split is negotiated in the deal room.</p>
          )}

          <div>
            <Label>Image</Label>
            <Input value={form.image_url?.startsWith("data:") ? "" : form.image_url} onChange={set("image_url")} placeholder="Paste image URL…" className="mb-2" />
            <div
              data-testid="deal-image-paste"
              onPaste={onImagePaste}
              tabIndex={0}
              className="flex items-center gap-3 rounded-md border border-dashed border-black/20 p-3 text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {form.image_url ? (
                <img src={form.image_url} alt="preview" className="h-14 w-14 rounded object-cover" />
              ) : (
                <ImagePlus className="h-6 w-6" />
              )}
              <div className="flex-1">
                <input type="file" accept="image/*" id="deal-image-file" data-testid="deal-image-file" className="hidden" onChange={(e) => onImageFile(e.target.files?.[0])} />
                <button type="button" onClick={() => document.getElementById("deal-image-file").click()} className="font-semibold text-primary">Upload</button>
                {" "}or paste an image here (or use a URL above).
              </div>
              {form.image_url && (
                <button type="button" data-testid="clear-image-btn" onClick={() => setVal("image_url", "")} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
              )}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Location</Label>
            <Select value={form.location_scope} onValueChange={(v) => setVal("location_scope", v)}>
              <SelectTrigger data-testid="deal-location-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="canada_wide">Canada-wide</SelectItem>
                <SelectItem value="province">Province</SelectItem>
                <SelectItem value="city">City</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(form.location_scope === "province" || form.location_scope === "city") && (
            <div>
              <Label>Province</Label>
              <Select value={form.province || ""} onValueChange={(v) => setVal("province", v)}>
                <SelectTrigger data-testid="deal-province-select"><SelectValue placeholder="Select province" /></SelectTrigger>
                <SelectContent>
                  {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.location_scope === "city" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input data-testid="deal-city-input" value={form.city} onChange={set("city")} /></div>
              <div><Label>Address</Label><Input data-testid="deal-address-input" value={form.address} onChange={set("address")} placeholder="Store address" /></div>
            </div>
          )}

          <div><Label>Deal expiry</Label><Input type="date" data-testid="deal-expiry-input" value={form.expires_at || ""} onChange={set("expires_at")} /></div>
          <div><Label>Source</Label><Input value={form.source} onChange={set("source")} placeholder="Weekly flyer / retailer app" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>

          <div className="rounded-md border border-black/10 p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox data-testid="both-present-checkbox" checked={form.both_present_required}
                onCheckedChange={(v) => setVal("both_present_required", !!v)} className="mt-0.5" />
              <span className="text-sm">{BOTH_PRESENT_TERM}</span>
            </label>
          </div>
          <div><Label>Additional terms</Label><Textarea value={form.terms} onChange={set("terms")} rows={2} placeholder="Any other conditions…" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="publish-deal-btn" onClick={saveDeal} disabled={busy || !form.title || !form.retailer} className="rounded-full">
            {editingId ? "Save changes" : "Publish deal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

DealFormDialog.displayName = "DealFormDialog";
export default DealFormDialog;
